import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { defaultTheme } from '../../../../../../utils/defaultTheme';
import {
    DropdownWrapper,
    MappingChartWrapper,
    ResetButton,
    SectionTitle,
    SectionTitleWrapper,
    SvgContainer,
    TitleWrapper,
    SectionDropdownWrapper,
} from './MappingChart.styles';
import * as SC_PA from '../../PreferanceAnalysis.styles';
import { useWindowSize } from '../../../../../../common/hooks/useWindowSize';
import { getChartAreasData, getLineLength, getMappingLabelsData, getMappingMainAxisLines } from './utilities';
import { initLabeler } from './Labeler';
import { calculateForResolution } from '../../../../../../utils/responsiveness';
import { TRANSLATION_TEXT } from '../../../../../../utils/translations';
import Dropdown from '../Dropdown/Dropdown';
import { basePath } from '../../../../../../services/utils';
import Tooltip from '../Tooltip/Tooltip';
import { createClassName } from '../../../../../../common/utils/utils';

const SVG_AREA_SIZE = {
    width: 1235,
    height: 895,
};
export const CHART_MARGIN = {
    top: 90,
    left: 90,
};

const AREA_COLORS = {
    GOOD_VALUE: '#FAF1FF',
    PREMIUM: '#E8F9ED',
    POOR_VALUE: '#FFECE6',
    LOW_COST: '#ECF6FF',
    GOOD_VALUE_TEXT: '#A157CC',
    PREMIUM_TEXT: '#1CC54E',
    POOR_VALUE_TEXT: '#FF6E40',
    LOW_COST_TEXT: '#243B5F',
};

const POINT_LINE_TO_LABEL_CUTOFF = 30;
const POINT_SIZE = 8;
const TOOLTIP_SIZE = {
    height: 107,
    width: 210,
};

const MappingChart = ({ data, listOptions, transitionData }) => {
    const svgRef = useRef(null);
    const initialDataChangedRef = useRef(false);
    const [isMappingChartUpdated, setIsMappingChartUpdated] = useState(false);
    const [keyDataTransition, setKeyDataTransition] = useState(null);
    const [keyAxisSelection, setKeyAxisSelection] = useState(null);
    const [chartListOptions, setChartListOptions] = useState(listOptions);
    const [updatedPointsArr, setUpdatedPointsArr] = useState([]);
    const [resetChart, setResetChart] = useState(false);
    const [isAxisChartUpdated, setIsAxisChartUpdated] = useState(false);
    const listOptionsAxis = {
        key: data.year,
        title: data.benchmarkLabel,
        itemLabel: `${data.benchmarkLabel}`,
        itemLabelUnmodified: data.benchmarkLabel,
        nrRespondents: data.nrRespondents,
        isCurrentYear: true,
        isChecked: false,
    };
    let labelsArray = [],
        anchorArray = [];
    const [width] = useWindowSize();

    const calculatedWidth = useMemo(() => {
        const calculated = calculateForResolution(width);
        return calculated > 1400 ? 1400 : calculated - 15;
    }, [width]);

    const calculatedHeight = useMemo(() => {
        const calculated = calculateForResolution(width);
        return calculated > 1400 ? SVG_AREA_SIZE.height : SVG_AREA_SIZE.height * 0.8;
    }, [width]);

    const xCoordinateScale = (xScaleData, innerWidth) => {
        return d3
            .scaleLinear()
            .domain([xScaleData.mainXAxisMin, xScaleData.mainXAxisMax])
            .range([CHART_MARGIN.left, innerWidth])
            .clamp(true);
    };
    const yCoordinateScale = (yScaleData, innerHeight) => {
        return d3
            .scaleLinear()
            .domain([yScaleData.mainYAxisMin, yScaleData.mainYAxisMax])
            .range([innerHeight, CHART_MARGIN.top])
            .clamp(true);
    };

    const getDifferenceArrayBetweenYearSelection = (currentData, comparisonData) => {
        const dataTransitionProducts = comparisonData.points.map(item => item.productName);
        const dataCurrentProducts = currentData.points.map(item => item.productName);

        const addedProducts = dataTransitionProducts.filter(item => !dataCurrentProducts.includes(item));

        const removedProducts = dataCurrentProducts.filter(item => !dataTransitionProducts.includes(item));
        //this array will contain all points that are existant in only one of the arrays (current or transition data)
        return [...addedProducts, ...removedProducts];
    };

    const drawAxis = (parent, xAxis, yAxis, size) => {
        parent
            .append('g')
            .attr('transform', `translate(0,${size.innerHeight})`)
            .attr('id', 'x-axis')
            .attr('class', 'axis')
            .call(xAxis);

        parent
            .append('g')
            .attr('transform', `translate(${CHART_MARGIN.left}, 0)`)
            .attr('id', 'y-axis')
            .attr('class', 'axis')
            .call(yAxis);

        parent
            .append('g')
            .attr('transform', `translate(${size.innerWidth}, 0)`)
            .attr('id', 'y-right-axis')
            .attr('class', 'axis')
            .call(yAxis);

        parent
            .append('g')
            .attr('transform', `translate(0,${CHART_MARGIN.top})`)
            .attr('id', 'x-right-axis')
            .attr('class', 'axis')
            .call(xAxis);

        parent.selectAll('.axis text').remove();
        parent.selectAll('.axis path').attr('stroke', defaultTheme.grey[800]);
    };

    const drawHelperAxis = (parent, dataRanges) => {
        const mainAxisData = getMappingMainAxisLines(CHART_MARGIN, dataRanges);

        const axisLines = parent.append('g').attr('class', 'mapping-helper-axis-lines');
        const gg2 = axisLines.selectAll('g').data(mainAxisData).enter().append('g');

        gg2.append('path')
            .attr('d', d => d3.line()(d.lines))
            .attr('stroke', defaultTheme.blue[900])
            .attr('stroke-width', 2)
            .style('marker-end', 'url(#Triangle)');

        gg2.append('text')
            .text(d => d.text.text)
            .style('font-size', '15px')
            .style('font-weight', 400)
            .style('color', defaultTheme.blue[900])
            .style('text-shadow', `0 0 1px ${defaultTheme.blue[900]}`)
            .style('transform', d => `translate(${d.text.x}px,${d.text.y}px) rotate(${d.text.rotate})`);
    };

    const drawOrUpdateAreas = (parent, dataRanges, areasData, isTransitionedAxis) => {
        const { meanX, maxX, minY, meanY } = dataRanges;

        // Draw and treat the transition of the 4 quadrans
        let chartAreas = parent.selectAll('rect.areasrect').data(areasData);
        chartAreas
            .enter()
            .append('rect')
            .attr('class', 'areasrect')
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .attr('width', d => d.width)
            .attr('height', d => d.height)
            .attr('fill', d => d.color)
            .merge(chartAreas)
            .transition()
            .duration(1000)
            .ease(d3.easeLinear)
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .attr('width', d => d.width)
            .attr('height', d => d.height)
            .attr('fill', d => d.color);

        // Mean X line
        let meanXLine = parent.selectAll('.mean-x-line').data([null]);
        meanXLine
            .enter()
            .append('line')
            .attr('class', 'mean-x-line')
            .style('stroke', defaultTheme.grey[800])
            .style('stroke-dasharray', '3.3')
            .attr('x1', CHART_MARGIN.left)
            .attr('y1', meanY)
            .attr('x2', maxX)
            .attr('y2', meanY)
            .merge(meanXLine)
            .transition()
            .duration(1000)
            .ease(d3.easeLinear)
            .style('stroke', isTransitionedAxis ? defaultTheme.violet[500] : defaultTheme.grey[800])
            .style('stroke-dasharray', '3.3')
            .attr('x1', CHART_MARGIN.left)
            .attr('y1', meanY)
            .attr('x2', maxX)
            .attr('y2', meanY);

        // Mean X arrow
        let meanXArrow = parent.selectAll('.mean-x-arrow').data([null]);
        meanXArrow
            .enter()
            .append('line')
            .attr('class', 'mean-x-arrow')
            .style('stroke-width', 2.3)
            .attr('x1', maxX)
            .attr('y1', meanY)
            .attr('x2', maxX)
            .attr('y2', meanY)
            .style('marker-end', 'url(#Triangle2)')
            .merge(meanXArrow)
            .transition()
            .duration(1000)
            .ease(d3.easeLinear)
            .attr('x1', maxX)
            .attr('y1', meanY)
            .attr('x2', maxX)
            .attr('y2', meanY);

        // Mean Y line
        let meanYLine = parent.selectAll('.mean-y-line').data([null]);
        meanYLine
            .enter()
            .append('line')
            .attr('class', 'mean-y-line')
            .style('stroke', defaultTheme.grey[800])
            .style('stroke-dasharray', '3.3')
            .attr('x1', meanX)
            .attr('y1', CHART_MARGIN.top)
            .attr('x2', meanX)
            .attr('y2', minY)
            .merge(meanYLine)
            .transition()
            .duration(1000)
            .ease(d3.easeLinear)
            .style('stroke', isTransitionedAxis ? defaultTheme.violet[500] : defaultTheme.grey[800])
            .style('stroke-dasharray', '3.3')
            .attr('x1', meanX)
            .attr('y1', CHART_MARGIN.top)
            .attr('x2', meanX)
            .attr('y2', minY);

        // Mean Y arrow
        let meanYArrow = parent.selectAll('.mean-y-arrow').data([null]);
        meanYArrow
            .enter()
            .append('line')
            .attr('class', 'mean-y-arrow')
            .style('stroke-width', 2.3)
            .attr('x1', meanX)
            .attr('y1', CHART_MARGIN.top)
            .attr('x2', meanX)
            .attr('y2', CHART_MARGIN.top)
            .style('marker-end', 'url(#Triangle3)')
            .merge(meanYArrow)
            .transition()
            .duration(1000)
            .ease(d3.easeLinear)
            .attr('x1', meanX)
            .attr('x2', meanX)
            .attr('y1', CHART_MARGIN.top)
            .attr('y2', CHART_MARGIN.top);
    };

    const drawLabels = (svg, parent, dataRanges, unscaledData) => {
        const onMouseOver = datum => {
            if (datum.tooltip) {
                const { lineClass, triangleId, x, xPos, yPos, paddindValue } = datum.tooltip;
                svg.select(lineClass).style('stroke-width', 2.1);
                svg.select(triangleId).select('path').style('fill', defaultTheme.black.ff);

                const tool = drawTooltip(
                    svg,
                    xPos - TOOLTIP_SIZE.width / 2 - 6,
                    yPos - TOOLTIP_SIZE.height,
                    paddindValue
                );

                tool.transition().duration(500).style('opacity', 1);
                tool.html(`
                    <h6>${TRANSLATION_TEXT.MAPPING_CHART_MEAN_TOOLTIP}</h6>
                    <p>${parseFloat(x).toFixed(4)}</p>
                `);
            }
        };
        const onMouseOut = datum => {
            if (datum.tooltip) {
                const { lineClass, triangleId } = datum.tooltip;
                svg.select(triangleId)
                    .select('path')
                    .style('fill', defaultTheme.white.ff)
                    .style('stroke', defaultTheme.black.ff);
                svg.select(lineClass).style('stroke-width', null);
                svg.select('.mapping-tooltip-foreign-object').remove();
            }
        };

        const checkChartLabelsGExistance = parent.select('.chart-labels').empty();

        //Labels for Areas (for each quadran)
        const labelsData = getMappingLabelsData(CHART_MARGIN, dataRanges, AREA_COLORS, unscaledData);
        //if is data selected from dropdown, update tooltip data and have transition to new position
        if (!checkChartLabelsGExistance) {
            labelsData.forEach(labelData => {
                const chartLabelForeignObject = parent
                    .select('.chart-labels')
                    .select(`.${createClassName(labelData.text)}`)
                    .select('foreignObject');
                chartLabelForeignObject
                    .select('span')
                    .on('mouseover', () => onMouseOver(labelData))
                    .on('mouseout', () => onMouseOut(labelData));
                chartLabelForeignObject
                    .transition()
                    .duration(1000)
                    .ease(d3.easeLinear)
                    .attr('x', labelData.x)
                    .attr('y', labelData.y);
            });
        } else {
            //construct chart-labels with the initial data provided
            const labels = parent.append('g').attr('class', 'chart-labels');
            const labelGroups = labels
                .selectAll('g')
                .data(labelsData)
                .enter()
                .append('g')
                .attr('class', d =>
                    d.tooltip ? `mean-group-label ${createClassName(d.text)}` : createClassName(d.text)
                );
            labelGroups
                .append('foreignObject')
                .attr('x', d => d.x)
                .attr('y', d => d.y)
                .attr('height', d => d.height + 2)
                .attr('width', d => d.width)
                .append('xhtml:div')
                .style('width', d => d.width)
                .style('height', d => d.height + 'px')
                .attr('class', 'mappingLabelGroup')
                .style('background', d => d.color)
                .style('border', d => `1px solid ${d.textColor}`)
                .append('xhtml:span')
                .attr('class', 'mappingLabelGroupText')
                .style('font-size', d => d.fontSize)
                .style('color', d => d.textColor)
                .html(d => d.text)
                .on('mouseover', (_, datum) => onMouseOver(datum))
                .on('mouseout', (_, datum) => onMouseOut(datum));
        }
    };

    const drawPoints = (svg, xScale, yScale, areasData) => {
        //Convert the data to a format which can be easily modified by the labeler
        let { points } = data;
        points.forEach((p, i) => {
            labelsArray.push({
                finalX: 0,
                finalY: 0,
                x: xScale(p.xValue),
                y: yScale(p.yValue),
                w: 0,
                h: 0,
                productName: p.productName,
                done: false,
                Image: p.imageUrl,
                xValue: parseFloat(p.xValue).toFixed(10),
                yValue: parseFloat(p.yValue).toFixed(10),
                label: areasData[i],
                year: data.year,
            });
            anchorArray.push({
                x: xScale(p.xValue),
                y: yScale(p.yValue),
                w: 0,
                h: 0,
                productName: p.productName,
            });
        });

        let gDots = svg
            .append('g')
            .attr('class', 'points-area')
            .selectAll('g.dot')
            .data(labelsArray)
            .enter()
            .append('g');

        // Add the diamond points and hover/click functionality
        let gPoints = gDots
            .append('rect')
            .attr('class', 'dot')
            .attr('id', d => d.productName.replace(/[/'\s+]/g, ''))
            .attr('width', POINT_SIZE)
            .attr('height', POINT_SIZE)
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .style('fill', defaultTheme.blue[900])
            .style('transform-box', 'fill-box')
            .style('transform', 'rotate(45deg)')
            .style('transform-origin', 'center')
            .style('cursor', 'pointer')
            .style('stroke-width', '1.6')
            .style('opacity', 0)
            .on('mouseover', function (_, datum) {
                onPointMouseOver(this, datum, svg, xScale, yScale, false);
            })
            .on('mouseout', function () {
                onPointMouseOut(this, svg, defaultTheme.blue[800]);
            });

        // Add the floating labels for each point
        let labels = gDots
            .append(d => {
                if (d.Image) {
                    return document.createElementNS('http://www.w3.org/2000/svg', 'image');
                } else {
                    return document.createElementNS('http://www.w3.org/2000/svg', 'text');
                }
            })
            .text(d => d.productName)
            .attr('class', 'floating-label')
            .attr('xlink:href', d => d.Image && d.Image)
            .attr('width', d => (d.Image ? 30 : null))
            .attr('x', d => d.x + 12)
            .attr('y', d => d.y + 4)
            .attr('fill', defaultTheme.blue[900])
            .style('font-weight', 600)
            .style('opacity', 0)
            .style('font-size', '12px');

        //Add the connection lines between each point and its floating label
        let links = gDots
            .append('line')
            .attr('class', 'mainBrandLink')
            .attr('x1', d => d.x)
            .attr('y1', d => d.y + POINT_SIZE / 2)
            .attr('x2', d => d.x)
            .attr('y2', d => d.y)
            .attr('stroke-width', 2)
            .attr('stroke', defaultTheme.grey[600]);

        // //After display get each label's and point's  width and height. Info needed for the labeler
        let index = 0;
        labels.each(function () {
            labelsArray[index].w = this.getBBox().width;
            labelsArray[index].h = this.getBBox().height * 2;
            index += 1;
        });

        let index2 = 0;
        gPoints.each(function () {
            anchorArray[index2].w = this.getBoundingClientRect().width;
            anchorArray[index2].h = this.getBoundingClientRect().height * 2;
            index2 += 1;
        });

        const labeler = initLabeler();
        // //Run the labeler which will modify the positions of the labels
        labeler().label(labelsArray).anchor(anchorArray).width(calculatedWidth).height(calculatedHeight).start();

        //Transition the labels into their proper positions
        gPoints.transition().duration(1000).style('opacity', 1);
        labels
            .transition()
            .duration(0)
            .style('opacity', 1)
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .style('pointer-events', 'none');
        links
            .transition()
            .duration(0)
            .attr('x2', function (d) {
                const prevX1 = d3.select(this).attr('x2');
                const prevY1 = d3.select(this).attr('y1');

                //Find optimal position of the line between the point and the label
                const beginLineLength = getLineLength(prevX1, d.x, prevY1, d.y);
                const midLineLength = getLineLength(prevX1, d.x + d.w / 2, prevY1, d.y);
                const endLineLength = getLineLength(prevX1, d.x + d.w, prevY1, d.y);

                const distancePointMapping = [
                    { size: beginLineLength, point: d.x },
                    { size: midLineLength, point: d.x + d.w / 2 },
                    { size: endLineLength, point: d.x + d.w },
                ];

                if (
                    beginLineLength < POINT_LINE_TO_LABEL_CUTOFF ||
                    endLineLength < POINT_LINE_TO_LABEL_CUTOFF ||
                    midLineLength < POINT_LINE_TO_LABEL_CUTOFF
                )
                    return prevX1;

                const min = Math.min(beginLineLength, midLineLength, endLineLength);

                return distancePointMapping.find(e => e.size === min).point;
            })
            .attr('y2', function (d) {
                const prevX1 = d3.select(this).attr('x1');
                const prevY1 = d3.select(this).attr('y1');

                //Find optimal position of the line between the point and the label
                const beginLineLength = getLineLength(prevX1, d.x, prevY1, d.y);
                const midLineLength = getLineLength(prevX1, d.x + d.w / 2, prevY1, d.y);
                const endLineLength = getLineLength(prevX1, d.x + d.w, prevY1, d.y);

                if (
                    beginLineLength < POINT_LINE_TO_LABEL_CUTOFF ||
                    endLineLength < POINT_LINE_TO_LABEL_CUTOFF ||
                    midLineLength < POINT_LINE_TO_LABEL_CUTOFF
                )
                    return prevY1;

                return d.y;
            })
            .style('pointer-events', 'none')
            .attr('marker-end', function (d) {
                const prevX1 = d3.select(this).attr('x1');
                const prevY1 = d3.select(this).attr('y1');

                //Find optimal position of the line between the point and the label
                const beginLineLength = getLineLength(prevX1, d.x, prevY1, d.y);
                const midLineLength = getLineLength(prevX1, d.x + d.w / 2, prevY1, d.y);
                const endLineLength = getLineLength(prevX1, d.x + d.w, prevY1, d.y);

                if (
                    beginLineLength < POINT_LINE_TO_LABEL_CUTOFF ||
                    endLineLength < POINT_LINE_TO_LABEL_CUTOFF ||
                    midLineLength < POINT_LINE_TO_LABEL_CUTOFF
                )
                    return '';
                return 'url(#Triangle)';
            });
    };

    const drawTooltip = (
        svg,
        x = SVG_AREA_SIZE.width / 2,
        y = SVG_AREA_SIZE.height / 2,
        paddingvalue,
        customWidth = TOOLTIP_SIZE.width
    ) => {
        /* Append the tooltip */
        return svg
            .append('foreignObject')
            .attr('class', 'mapping-tooltip-foreign-object')
            .style('pointer-events', 'none')
            .style('padding-top', paddingvalue)
            .attr('x', x)
            .attr('y', y)
            .attr('height', TOOLTIP_SIZE.height)
            .attr('width', customWidth)
            .append('xhtml:div')
            .attr('class', 'mapping-tooltip')
            .style('opacity', 0);
    };

    const onPointMouseOver = (el, datum, svgWrapper, horizontalScale, verticalScale, isTransitionPoint) => {
        const { xValue, yValue, productName, year } = datum;
        const paddingValue = 16;
        const tool = drawTooltip(
            svgWrapper,
            horizontalScale(xValue) - TOOLTIP_SIZE.width / 2 + POINT_SIZE / 2,
            verticalScale(yValue) - TOOLTIP_SIZE.height * 1.02,
            paddingValue
        );
        const className = isTransitionPoint ? 'class="transitioned"' : '';
        tool.transition('hover').duration(500).style('opacity', 1);
        tool.html(`
            <h6 ${className}>${
            productName.length >= 25 ? productName.substring(0, 20) + '...' : productName
        } (${year})</h6>
            <p ${className}>x: ${parseFloat(xValue).toFixed(4)}</p>
            <p ${className}>y: ${parseFloat(yValue).toFixed(4)}</p>
        `);

        /* Point animation */
        d3.select(el)
            .transition('hover')
            .duration(100)
            .attr('width', POINT_SIZE)
            .attr('height', POINT_SIZE)
            .style('fill', defaultTheme.red[400]);
        d3.select(el).transition('hover').duration(300).style('fill', defaultTheme.red[400]);
    };

    const onPointMouseOut = (el, svgWrapper, color) => {
        //clear tooltip
        svgWrapper.select('.mapping-tooltip-foreign-object').remove();
        d3.select(el)
            .transition('hover')
            .duration('300')
            .attr('width', POINT_SIZE)
            .attr('height', POINT_SIZE)
            .style('fill', color);
    };

    const updatePointsPosition = (svg, xScaleInitial, yScaleInitial, xScale, yScale, data2, data1) => {
        let updatedPointsArray = [];
        const allDifferences = getDifferenceArrayBetweenYearSelection(data1, data2);
        //in some cases there may appear in transition data elements that corespond to certain years only
        //but not have data in all years from transition
        const updatedProductNames = new Set(updatedPointsArr.map(p => p.productName));
        const data2WithExistFlag = data2.points
            .map(point => ({
                ...point,
                existsInUpdated: updatedProductNames.has(point.productName),
            }))
            .filter(item => !item.existsInUpdated);

        const drawNewPoints = el => {
            el.style('transform-box', 'fill-box')
                .style('transform', 'rotate(45deg)')
                .style('transform-origin', 'center')
                .style('fill', defaultTheme.violet[500])
                .style('cursor', 'pointer')
                .on('mouseover', function (_, datum) {
                    onPointMouseOver(this, datum, svg, xScale, yScale, true);
                })
                .on('mouseout', function () {
                    onPointMouseOut(this, svg, defaultTheme.violet[500]);
                })
                .transition()
                .duration(2000)
                .ease(d3.easeLinear)
                .attr('x', d => d.finalX)
                .attr('y', d => d.finalY);
        };

        data2.points.forEach(newPoint => {
            const initialPoint = data1.points.find(e => e.productName === newPoint.productName);

            let oldX, oldY;
            if (initialPoint) {
                oldX = xScaleInitial(initialPoint.xValue);
                oldY = yScaleInitial(initialPoint.yValue);
            } else {
                oldX = xScale(newPoint.xValue);
                oldY = yScale(newPoint.yValue);
            }

            updatedPointsArray.push({
                oldX,
                oldY,
                x: xScale(newPoint.xValue),
                y: yScale(newPoint.yValue),
                finalX: xScale(newPoint.xValue),
                finalY: yScale(newPoint.yValue),
                productName: newPoint.productName,
                imageUrl: newPoint.imageUrl,
                xValue: parseFloat(newPoint.xValue).toFixed(10),
                yValue: parseFloat(newPoint.yValue).toFixed(10),
                year: data2.year,
            });
        });

        setUpdatedPointsArr(updatedPointsArray);
        const checkGroupExistance = svg.selectAll('.points-area-transition').empty();

        // Create a new group to hold all point groups
        const parentGroup = checkGroupExistance
            ? svg.append('g').attr('class', 'points-area-transition')
            : svg.selectAll('.points-area-transition');

        // For each point, create a <g> group
        const pointGroups = parentGroup
            .selectAll('g.transition-group')
            .data(
                updatedPointsArray.filter(item => !allDifferences.includes(item.productName)),
                d => d.productName
            )
            .join('g')
            .attr('class', 'transition-group');

        // Append/update the new-dot rect to each group
        const newPoint = pointGroups
            .selectAll('rect.new-dot')
            .data(d => [d])
            .join('rect')
            .attr('class', 'new-dot')
            .attr('width', POINT_SIZE)
            .attr('height', POINT_SIZE);

        if (checkGroupExistance || data2WithExistFlag.length > 0) {
            newPoint.attr('x', d => d.oldX).attr('y', d => d.oldY);
        }
        drawNewPoints(newPoint);

        // Append/update the movement-line to each group if needed
        const newMovementLine = pointGroups
            .selectAll('line.movement-line')
            .data(d => (d.oldX !== d.x || d.oldY !== d.y ? [d] : []))
            .join('line');

        if (checkGroupExistance || data2WithExistFlag.length > 0) {
            newMovementLine
                .attr('x1', d => d.oldX)
                .attr('y1', d => d.oldY)
                .attr('x2', d => d.oldX)
                .attr('y2', d => d.oldY);
        }

        newMovementLine
            .attr('class', 'movement-line')
            .attr('stroke', defaultTheme.violet[500])
            .attr('stroke-dasharray', '4')
            .transition()
            .duration(2000)
            .ease(d3.easeLinear)
            .attr('x1', d => d.oldX + 5)
            .attr('y1', d => d.oldY)
            .attr('x2', d => d.x + 5)
            .attr('y2', d => d.y);

        //floating points - points that exist only in transition data
        const floatingPoints = parentGroup
            .selectAll('.floating-point')
            .data(updatedPointsArray.filter(item => allDifferences.includes(item.productName)));

        const newFloatingPoint = floatingPoints
            .enter()
            .append('rect')
            .attr('class', 'floating-point')
            .attr('width', POINT_SIZE)
            .attr('height', POINT_SIZE)
            .attr('x', d => d.oldX)
            .attr('y', d => d.oldY)
            .merge(floatingPoints);
        drawNewPoints(newFloatingPoint);

        floatingPoints.exit().remove();
    };

    const updateLabelsPosition = (svg, xScaleInitial, yScaleInitial, xScale, yScale, data2, data1) => {
        let updatedLabelsPosition = [];
        let anchors = [];

        const allDifferences = getDifferenceArrayBetweenYearSelection(data1, data2);
        const productNameSet = new Set([
            ...data1.points.map(p => p.productName),
            ...data2.points.map(p => p.productName),
        ]);

        productNameSet.forEach(productName => {
            const oldPoint = data1.points.find(p => p.productName === productName);
            const newPoint = data2.points.find(p => p.productName === productName);

            let oldX, oldY, newX, newY;
            if (oldPoint) {
                oldX = xScaleInitial(oldPoint.xValue);
                oldY = yScaleInitial(oldPoint.yValue);
            }
            if (newPoint) {
                newX = xScale(newPoint.xValue);
                newY = yScale(newPoint.yValue);
            }

            updatedLabelsPosition.push({
                x: newX !== undefined ? newX : oldX,
                y: newY !== undefined ? newY : oldY,
                finalX: newX !== undefined ? newX : oldX,
                finalY: newY !== undefined ? newY : oldY,
                hasChange: newPoint ? true : false,
                productName,
                w: 0,
                h: 0,
            });

            anchors.push({
                x: newX ?? oldX,
                y: newY ?? oldY,
                w: 0,
                h: 0,
                productName,
            });
        });

        const pastProductsData = updatedLabelsPosition.filter(item => allDifferences.includes(item.productName));
        //if there is data that is in the past years but not in current data, creat a label for this data
        const pastLabels = svg
            .selectAll('.points-area-transition')
            .selectAll('.past-product-label')
            .data(pastProductsData, d => d.productName);
        pastLabels
            .enter()
            .append('text')
            .attr('class', 'floating-label past-product-label')
            .text(d => d.productName)
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .style('fill', defaultTheme.violet[500])
            .style('font-weight', 600)
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 1);

        //after creating the labels from previous years(if they exist) do the calculate of the position of all floating-label elements
        const labels = svg.selectAll('.floating-label').data(updatedLabelsPosition, d => d.productName);
        labels.each(function (d, i) {
            const bbox = this.getBBox();
            updatedLabelsPosition[i].w = bbox.width;
            updatedLabelsPosition[i].h = bbox.height * 2;
            anchors[i].w = bbox.width;
            anchors[i].h = bbox.height * 2;
        });

        const labeler = initLabeler();
        labeler().label(updatedLabelsPosition).anchor(anchors).width(calculatedWidth).height(calculatedHeight).start();
        labels
            .merge(labels)
            .transition()
            .duration(2000)
            .style('fill', d => (d.hasChange ? defaultTheme.violet[500] : defaultTheme.blue[900]))
            .style('font-weight', 600)
            .style('font-size', '12px')
            .ease(d3.easeLinear)
            .attr('x', d => d.finalX)
            .attr('y', d => d.finalY - 5);
        labels.exit().remove();
    };

    const renderChart = (svg, chartWidth, chartHeight) => {
        const innerWidth = chartWidth - CHART_MARGIN.left;
        const innerHeight = chartHeight - CHART_MARGIN.top;

        const xScale = xCoordinateScale(data, innerWidth);
        const yScale = yCoordinateScale(data, innerHeight);

        const scaledDataRanges = {
            minX: xScale(data.mainXAxisMin),
            meanX: xScale(data.xAxisMean),
            maxX: xScale(data.mainXAxisMax),
            minY: yScale(data.mainYAxisMin),
            meanY: yScale(data.yAxisMean),
            maxY: yScale(data.mainYAxisMax),
        };

        const xAxis = d3.axisBottom(xScale).tickSize(0);
        const yAxis = d3.axisLeft(yScale).tickSize(0);

        const areas = svg.append('g').attr('class', 'chart-areas');
        const areasData = getChartAreasData(CHART_MARGIN, scaledDataRanges, AREA_COLORS);

        drawOrUpdateAreas(areas, scaledDataRanges, areasData, false);
        drawAxis(svg, xAxis, yAxis, { innerWidth, innerHeight });
        drawHelperAxis(svg, scaledDataRanges);
        drawLabels(svg, areas, scaledDataRanges, data);
        drawPoints(svg, xScale, yScale, areasData);
    };
    const axisChartInitialState = selectedTransitionData => {
        if (svgRef && svgRef.current) {
            const innerWidth = calculatedWidth - CHART_MARGIN.left;
            const innerHeight = calculatedHeight - CHART_MARGIN.top;
            const svg = d3.select(svgRef.current);
            const xScaleInitial = xCoordinateScale(data, innerWidth);
            const yScaleInitial = yCoordinateScale(data, innerHeight);
            const xScaleTransition = xCoordinateScale(data, innerWidth);
            const yScaleTransition = yCoordinateScale(data, innerHeight);

            const scaledDataRanges = {
                minX: xScaleInitial(data.mainXAxisMin),
                meanX: xScaleTransition(data.xAxisMean),
                maxX: xScaleTransition(data.mainXAxisMax),
                minY: yScaleInitial(data.mainYAxisMin),
                meanY: yScaleTransition(data.yAxisMean),
                maxY: yScaleTransition(data.mainYAxisMax),
            };
            const areasData = getChartAreasData(CHART_MARGIN, scaledDataRanges, AREA_COLORS);

            drawOrUpdateAreas(svg, scaledDataRanges, areasData, false);
            drawLabels(svg, svg.select('.chart-areas'), scaledDataRanges, selectedTransitionData);
        }
    };
    const handleUpdateChart = selectedTransitionData => {
        if (isAxisChartUpdated) {
            //if the axis chart is updated and the points position dropdown is changed, revert the axis to the default coordinastes (the ones from initial data)
            axisChartInitialState(selectedTransitionData);
        }
        if (svgRef && svgRef.current) {
            const innerWidth = calculatedWidth - CHART_MARGIN.left;
            const innerHeight = calculatedHeight - CHART_MARGIN.top;
            const svg = d3.select(svgRef.current);

            const xScaleInitial = xCoordinateScale(data, innerWidth);
            const yScaleInitial = yCoordinateScale(data, innerHeight);
            const xScaleTransition = xCoordinateScale(selectedTransitionData, innerWidth);
            const yScaleTransition = yCoordinateScale(selectedTransitionData, innerHeight);
            svg.selectAll('.mainBrandLink').remove();

            updatePointsPosition(
                svg,
                xScaleInitial,
                yScaleInitial,
                xScaleTransition,
                yScaleTransition,
                selectedTransitionData,
                data
            );
            updateLabelsPosition(
                svg,
                xScaleInitial,
                yScaleInitial,
                xScaleTransition,
                yScaleTransition,
                selectedTransitionData,
                data
            );

            setIsAxisChartUpdated(false);
        }
    };
    const handleUpdateChartAxis = selectedTransitionData => {
        if (svgRef && svgRef.current) {
            const innerWidth = calculatedWidth - CHART_MARGIN.left;
            const innerHeight = calculatedHeight - CHART_MARGIN.top;
            const svg = d3.select(svgRef.current);
            const xScaleInitial = xCoordinateScale(data, innerWidth);
            const yScaleInitial = yCoordinateScale(data, innerHeight);
            const xScaleTransition = xCoordinateScale(selectedTransitionData, innerWidth);
            const yScaleTransition = yCoordinateScale(selectedTransitionData, innerHeight);

            const scaledDataRanges = {
                minX: xScaleInitial(data.mainXAxisMin),
                meanX: xScaleTransition(selectedTransitionData.xAxisMean),
                maxX: xScaleTransition(selectedTransitionData.mainXAxisMax),
                minY: yScaleInitial(data.mainYAxisMin),
                meanY: yScaleTransition(selectedTransitionData.yAxisMean),
                maxY: yScaleTransition(selectedTransitionData.mainYAxisMax),
            };
            const areasData = getChartAreasData(CHART_MARGIN, scaledDataRanges, AREA_COLORS);

            drawOrUpdateAreas(svg, scaledDataRanges, areasData, true);
            drawLabels(svg, svg.select('.chart-areas'), scaledDataRanges, selectedTransitionData);
        }
    };
    const clearDefsElements = el => {
        el.selectAll('*')
            .filter(function () {
                return !this.classList.contains('defs');
            })
            .remove();
        el.selectAll('.movement-line').remove();
    };

    useEffect(() => {
        if (!data) return;

        initialDataChangedRef.current = true;
        setIsMappingChartUpdated(false);
        setKeyDataTransition(null);
        setChartListOptions(prevOptions => prevOptions.map(option => ({ ...option, isChecked: false })));
        if (svgRef && svgRef.current && resetChart) {
            const svg = d3.select(svgRef.current);
            clearDefsElements(svg);
            renderChart(svg, calculatedWidth, calculatedHeight);
            setResetChart(false);
        }
    }, [data, resetChart]);

    useEffect(() => {
        if (!svgRef.current || !data) return;

        const svg = d3.select(svgRef.current);
        clearDefsElements(svg);
        renderChart(svg, calculatedWidth, calculatedHeight);

        if (initialDataChangedRef.current) {
            initialDataChangedRef.current = false;
            return;
        }

        if (isMappingChartUpdated && keyDataTransition) {
            const selectedTransitionData = transitionData.find(el => el.year === parseInt(keyDataTransition, 10));
            if (selectedTransitionData) {
                handleUpdateChart(selectedTransitionData);
            }
        }
    }, [calculatedWidth, calculatedHeight, data]);

    const handleYearChange = key => {
        setChartListOptions(prevOptions =>
            prevOptions.map(option =>
                option.key === key ? { ...option, isChecked: true } : { ...option, isChecked: false }
            )
        );
        setKeyDataTransition(key);
        setIsMappingChartUpdated(true);
        const selectedTransitionData = transitionData.find(el => el.year === parseInt(key, 10));
        handleUpdateChart(selectedTransitionData);
    };
    const handleYearChangeAxis = key => {
        setKeyAxisSelection(key);
        if (key === data.year) {
            axisChartInitialState([]);
        } else {
            const selectedTransitionData = transitionData.find(el => el.year === parseInt(key, 10));
            handleUpdateChartAxis(selectedTransitionData);
            setIsAxisChartUpdated(true);
        }
    };

    const filterCheckOption = arr => arr.filter(item => item.isChecked)[0];
    const chartAxisOptions = chartListOptions.filter(option => option.isChecked).concat(listOptionsAxis);
    return (
        <>
            <MappingChartWrapper>
                <SectionTitleWrapper isMappingChartUpdated={isMappingChartUpdated}>
                    {listOptions && listOptions.length > 0 && chartListOptions.length > 0 && (
                        <SectionDropdownWrapper isMappingChartUpdated={isMappingChartUpdated}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '20px' }}>
                                <DropdownWrapper>
                                    <Dropdown
                                        options={chartListOptions}
                                        selectedOption={filterCheckOption(chartListOptions)?.key}
                                        onChange={handleYearChange}
                                        defaultText={TRANSLATION_TEXT.MAPPING_CHART_DROPDOWN_DEFAULT}
                                        hasColorLine={false}
                                    />
                                </DropdownWrapper>
                                <Dropdown
                                    options={chartAxisOptions.map(option => ({
                                        ...option,
                                        itemLabel: option.itemLabelUnmodified,
                                    }))}
                                    selectedOption={keyAxisSelection}
                                    onChange={handleYearChangeAxis}
                                    defaultText={TRANSLATION_TEXT.MAPPING_CHART_DROPDOWN_AXIS}
                                    hasColorLine={false}
                                    disabled={keyDataTransition === null}
                                />
                            </div>
                            <ResetButton
                                style={{ display: isMappingChartUpdated ? 'block' : 'none' }}
                                onClick={() => setResetChart(true)}
                            >
                                {TRANSLATION_TEXT.MAPPING_CHART_RESET_BUTTON}
                            </ResetButton>
                        </SectionDropdownWrapper>
                    )}

                    <TitleWrapper>
                        <SectionTitle style={{ color: defaultTheme.blue[950] }}>
                            <span>{data.benchmarkLabel}</span>
                            <Tooltip
                                tooltipText={TRANSLATION_TEXT.INFO_PICTOGRAM_TEXT}
                                customStyle={{
                                    _content: {
                                        width: '230px',
                                        marginLeft: '3px',
                                        whiteSpace: 'pre-line',
                                        wordWrap: 'break-word',
                                        top: '-100px',
                                    },
                                }}
                            >
                                <SC_PA.InfoPic style={{ marginRight: '5px', display: 'flex' }}>
                                    <img src={`${basePath}/visuals/InfoIconYTD.png`} />
                                </SC_PA.InfoPic>
                            </Tooltip>
                            <span>{`n=${data.nrRespondents}`}</span>
                        </SectionTitle>
                        {isMappingChartUpdated && (
                            <>
                                <SectionTitle style={{ color: defaultTheme.violet[500] }}>{'vs'}</SectionTitle>
                                <SectionTitle style={{ color: defaultTheme.violet[500] }}>
                                    <span style={{ marginRight: '5px' }}>
                                        {filterCheckOption(chartListOptions).title}
                                    </span>
                                    <span>{`n=${filterCheckOption(chartListOptions).nrRespondents}`}</span>
                                </SectionTitle>
                            </>
                        )}
                    </TitleWrapper>
                </SectionTitleWrapper>
                <SvgContainer ref={svgRef} width={calculatedWidth} height={SVG_AREA_SIZE.height}>
                    <defs className={'defs'}>
                        <marker className={'defs'} id="Triangle" viewBox="0 0 10 10" refX="0" refY="5" orient="auto">
                            <path
                                className={'defs'}
                                d="M 0 0 L 10 5 L 0 10 z"
                                style={{ fill: defaultTheme.black.ff, stroke: 'none' }}
                            />
                        </marker>
                        <marker className={'defs'} id="Triangle2" viewBox="0 0 10 10" refX="0" refY="5" orient="180">
                            <path
                                className={'defs'}
                                d="M 0 0 L 10 5 L 0 10 z"
                                style={{ fill: defaultTheme.white.ff, stroke: defaultTheme.black.ff }}
                            />
                        </marker>
                        <marker className={'defs'} id="Triangle3" viewBox="0 0 10 10" refX="0" refY="5" orient="90">
                            <path
                                className={'defs'}
                                d="M 0 0 L 10 5 L 0 10 z"
                                style={{ fill: defaultTheme.white.ff, stroke: defaultTheme.black.ff }}
                            />
                        </marker>
                    </defs>
                </SvgContainer>
            </MappingChartWrapper>
        </>
    );
};

export default MappingChart;
